"use client"
import React, { useEffect, useRef } from "react"

const NAV_SEARCH_PARAM = "search"
const RAINFOCUS_SEARCH_PARAM = "search-rainfocus"

export interface RainfocusWidgetProps {
  apiToken: string
  widgetId: string
  env?: "prod" | "staging" | "dev"
}

declare global {
  interface Window {
    jQuery?: any
    Rainfocus?: {
      Widget: new (config: {
        apiToken: string
        widgetId: string
        env: string
      }) => any
    }
    widget?: any
  }
}

export const RainfocusWidget: React.FC<RainfocusWidgetProps> = ({
  apiToken,
  widgetId,
  env = "prod",
}) => {
  const widgetRef = useRef<HTMLDivElement>(null)
  const scriptsLoadedRef = useRef(false)

  // The catalog syncs its filter term to `?search=`, the same param the site
  // nav deep-links its Algolia search with, so typing here used to open the
  // nav's search overlay. Rename the catalog's writes to keep them separate.
  useEffect(() => {
    // Scoped to the bundle's own calls so the nav keeps deep-linking `search`.
    const fromRainfocus = () => (new Error().stack ?? "").includes("rainfocus")

    const rename = (url: Parameters<History["pushState"]>[2]) => {
      if (url == null || !fromRainfocus()) return url
      const next = new URL(String(url), window.location.href)
      const term = next.searchParams.get(NAV_SEARCH_PARAM)
      if (term === null) return url
      next.searchParams.delete(NAV_SEARCH_PARAM)
      next.searchParams.set(RAINFOCUS_SEARCH_PARAM, term)
      return `${next.pathname}${next.search}${next.hash}`
    }

    const { pushState, replaceState } = window.history
    window.history.pushState = function (state, title, url) {
      return pushState.call(this, state, title, rename(url))
    }
    window.history.replaceState = function (state, title, url) {
      return replaceState.call(this, state, title, rename(url))
    }
    return () => {
      window.history.pushState = pushState
      window.history.replaceState = replaceState
    }
  }, [])

  useEffect(() => {
    const loadRainfocusResources = async () => {
      if (scriptsLoadedRef.current) return

      try {
        // Load jQuery/ CSS Files // Rainfocus Bundle
        if (!window.jQuery) {
          await loadScript(
            "https://ajax.googleapis.com/ajax/libs/jquery/3.6.1/jquery.min.js",
          )
        }

        await loadCSS(
          "https://static.rainfocus.com/gfonts/css/e47fce46a9d2402094879d2f861fb21e.css",
        )
        await loadCSS(
          "https://events.rainfocus.com/scripts/catalog/bundle.min.css",
        )
        await loadCSS(
          "https://events.rainfocus.com/assets/css/rainfocusWidgets.css",
        )

        await loadScript("https://events.rainfocus.com/js/bundle.js")

        scriptsLoadedRef.current = true
        initializeWidget()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        // console.error('Failed to load Rainfocus resources:', error)
      }
    }

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve()
          return
        }

        const script = document.createElement("script")
        script.src = src
        script.onload = () => resolve()
        script.onerror = () =>
          reject(new Error(`Failed to load script: ${src}`))
        document.head.appendChild(script)
      })
    }

    const loadCSS = (href: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`link[href="${href}"]`)) {
          resolve()
          return
        }

        const link = document.createElement("link")
        link.rel = "stylesheet"
        link.href = href
        link.onload = () => resolve()
        link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`))
        document.head.appendChild(link)
      })
    }

    const initializeWidget = () => {
      if (window.Rainfocus && widgetRef.current) {
        try {
          window.widget = new window.Rainfocus.Widget({
            apiToken,
            widgetId,
            env,
          })
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          // console.error('Failed to initialize Rainfocus widget:', error)
        }
      }
    }

    loadRainfocusResources()

    // Cleanup function
    return () => {
      if (window.widget) {
        // Clean up widget if needed
        window.widget = undefined
      }
    }
  }, [apiToken, widgetId, env])

  return <div ref={widgetRef} className="rf-widget" id="rf-catalog" />
}